import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { authService } from "../../services/authService";
import { binService } from "../../services/binService";
import { mergeAnonymousBinIntoBackend, completePendingAgentAssignment } from "../../utils/postAuthMerge";
import { preAuthIntent } from "../../utils/preAuthIntent";
import StateSelector from "../Common/StateSelector";
import logo from "../../assets/imgs/jhonks_green_logo-removebg.png";
import signUpImage from "../../assets/imgs/sign-up-image.png";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

const Register = () => {
  const formatPhoneNumberForApi = (value = "") => {
    if (!value) return "";
    let digits = value.replace(/\D/g, "");

    if (digits.startsWith("234")) {
      digits = digits.slice(3);
    }

    // Preferred UI input: 0XXXXXXXXXX (11 digits)
    if (digits.startsWith("0") && digits.length === 11) {
      return `234${digits.slice(1)}`;
    }

    // Fallback: allow 10 digits (no leading 0)
    if (!digits.startsWith("0") && digits.length === 10) {
      return `234${digits}`;
    }

    return "";
  };

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    homeAddress: "",
    state: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
  });
  const [loading, setLoading] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState("");
  const [phoneOtpMessage, setPhoneOtpMessage] = useState("");
  const [phoneOtpError, setPhoneOtpError] = useState("");
  const [phoneOtpSending, setPhoneOtpSending] = useState(false);
  const [phoneOtpCooldownSeconds, setPhoneOtpCooldownSeconds] = useState(0);
  const [phoneOtpVerifying, setPhoneOtpVerifying] = useState(false);
  const [phoneVerificationToken, setPhoneVerificationToken] = useState("");
  const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneNumberExists, setPhoneNumberExists] = useState(false);
  const [step, setStep] = useState("phone");
  const otpRefs = useRef([]);
  const { register, error, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (phoneOtpCooldownSeconds <= 0) return undefined;
    const id = window.setInterval(() => {
      setPhoneOtpCooldownSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [phoneOtpCooldownSeconds]);

  const handleChange = (e) => {
    if (e.target.name === "phoneNumber") {
      setPhoneOtpSent(false);
      setPhoneOtp("");
      setPhoneOtpMessage("");
      setPhoneOtpError("");
      setPhoneOtpVerified(false);
      setPhoneVerificationToken("");
      setPhoneNumberExists(false);
      setPhoneOtpCooldownSeconds(0);

      let digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 11);
      setFormData({
        ...formData,
        phoneNumber: digitsOnly,
      });
      return;
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== "details") {
      if (!phoneOtpSent) await handleSendPhoneOtp();
      else await handleVerifyPhoneOtp();
      return;
    }
    setLoading(true);
    clearError();

    if (formData.password !== formData.confirmPassword) {
      alert("Passwords do not match");
      setLoading(false);
      return;
    }

    if (!phoneOtpVerified || !phoneVerificationToken) {
      setPhoneOtpError(
        "Please verify your phone number before creating an account."
      );
      setLoading(false);
      return;
    }

    try {
      const sanitizedPhone = formatPhoneNumberForApi(formData.phoneNumber);
      const email = formData.email.trim();
      await register({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        homeAddress: formData.homeAddress.trim(),
        state: formData.state,
        phoneNumber: sanitizedPhone,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        phoneVerificationToken,
        ...(email ? { email } : {}),
        ...(formData.referralCode.trim()
          ? { referralCode: formData.referralCode.trim() }
          : {}),
      });

      try {
        await mergeAnonymousBinIntoBackend(binService);
      } catch (mergeErr) {
        console.error("Anonymous bin merge error:", mergeErr);
      }

      const pendingAgentId = preAuthIntent.getPendingAgent();
      if (pendingAgentId) {
        await completePendingAgentAssignment({ binService, navigate });
        return;
      }

      navigate("/", { replace: true });
    } catch (error) {
      console.error("Registration error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPhoneOtp = async () => {
    const sanitizedPhone = formatPhoneNumberForApi(formData.phoneNumber);

    if (!sanitizedPhone) {
      setPhoneOtpError("Please enter your phone number to receive a code.");
      return;
    }

    if (phoneOtpCooldownSeconds > 0) {
      return;
    }

    setPhoneOtpSending(true);
    setPhoneOtpCooldownSeconds(60);
    setPhoneOtpError("");
    setPhoneOtpMessage("");
    setPhoneOtp("");
    setPhoneOtpVerified(false);
    setPhoneVerificationToken("");
    setPhoneNumberExists(false);

    try {
      const response = await authService.requestPhoneVerificationOtp({
        phoneNumber: sanitizedPhone,
      });
      setPhoneOtpSent(true);
      setPhoneOtpMessage(
        response.message || "We sent a 6-digit code to your WhatsApp. If it does not arrive, check your text messages."
      );
      setPhoneNumberExists(false);
    } catch (err) {
      const existingNumber =
        err.response?.data?.code === "PHONE_NUMBER_EXISTS" ||
        err.response?.data?.message?.toLowerCase?.().includes("already registered");
      if (existingNumber) {
        setPhoneNumberExists(true);
        setPhoneOtpSent(false);
        setPhoneOtpVerified(false);
        setPhoneVerificationToken("");
      }
      setPhoneOtpError(
        err.response?.data?.message ||
          "We couldn't send the verification code. Please try again."
      );
    } finally {
      setPhoneOtpSending(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    const sanitizedPhone = formatPhoneNumberForApi(formData.phoneNumber);

    if (!sanitizedPhone) {
      setPhoneOtpError("Please enter your phone number to receive a code.");
      return;
    }

    if (phoneOtp.length !== 6) {
      setPhoneOtpError("Please enter the 6-digit verification code.");
      return;
    }

    setPhoneOtpVerifying(true);
    setPhoneOtpError("");
    setPhoneOtpMessage("");

    try {
      const response = await authService.verifyPhoneVerificationOtp({
        phoneNumber: sanitizedPhone,
        otp: phoneOtp,
      });
      setPhoneVerificationToken(response.data?.verificationToken || "");
      setPhoneOtpVerified(Boolean(response.data?.phoneVerified));
      setPhoneOtpMessage(
        response.message || "Phone number verified successfully."
      );
      setStep("details");
    } catch (err) {
      setPhoneOtpError(
        err.response?.data?.message ||
          "We couldn't verify that code. Please try again."
      );
    } finally {
      setPhoneOtpVerifying(false);
    }
  };

  const updateOtpAt = (index, text) => {
    const digits = String(text || "").replace(/\D/g, "");
    if (digits.length > 1) {
      const merged = `${phoneOtp.slice(0, index)}${digits}`.replace(/\D/g, "").slice(0, 6);
      setPhoneOtp(merged);
      const focusAt = Math.min(merged.length, 5);
      window.requestAnimationFrame(() => otpRefs.current[focusAt]?.focus());
      return;
    }
    const next = Array.from({ length: 6 }, (_, i) => phoneOtp[i] || "");
    next[index] = digits.slice(-1);
    setPhoneOtp(next.join(""));
    if (digits && index < 5) otpRefs.current[index + 1]?.focus();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="hidden md:block relative">
          <img
            src={signUpImage}
            alt="Jhonks sign up"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/65 via-green-800/35 to-black/40" />
          <div className="absolute bottom-10 left-10 right-10 text-white">
            <h3 className="text-3xl font-bold leading-tight">Start selling with Jhonks</h3>
            <p className="mt-3 text-sm text-green-100">
              Create your account and begin turning waste materials into value.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center px-4 py-10 sm:px-8 lg:px-12">
      <div className="max-w-md w-full">
        <div>
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={logo}
              alt="Jhonks Logo"
              className="h-16 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {step === "phone" ? "Verify your number" : "Finish your account"}
          </h2>
          <p className="mt-1 text-center text-xs font-medium uppercase tracking-wide text-green-700">
            Step {step === "phone" ? "1" : "2"} of 2
          </p>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{" "}
            <Link
              to="/login"
              className="font-medium text-green-600 hover:text-green-500"
            >
              sign in to your existing account
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className={step === "details" ? "grid grid-cols-2 gap-4" : "hidden"}>
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  First Name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required={step === "details"}
                  placeholder="First name"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Last Name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required={step === "details"}
                  placeholder="Last name"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={step === "details" ? "" : "hidden"}>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email <span className="text-gray-500 text-xs font-normal">(Optional)</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email address"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className={step === "details" ? "" : "hidden"}>
              <label
                htmlFor="homeAddress"
                className="block text-sm font-medium text-gray-700"
              >
                Home Address
              </label>
              <input
                id="homeAddress"
                name="homeAddress"
                type="text"
                required={step === "details"}
                placeholder="Enter your home address"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                value={formData.homeAddress}
                onChange={handleChange}
              />
            </div>

            <div className={step === "details" ? "" : "hidden"}>
              <StateSelector
                value={formData.state}
                onChange={(state) => setFormData({ ...formData, state })}
                placeholder="Select your state"
                required={step === "details"}
              />
            </div>

            <div className={step === "phone" ? "" : "hidden"}>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-700"
              >
                Phone Number
              </label>
              <div className="mt-1 flex flex-col space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                  <div className="flex w-full">
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      required={step === "phone"}
                      placeholder="08148344959"
                      className="flex-1 min-w-0 appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      maxLength={11}
                      inputMode="numeric"
                      autoComplete="tel"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Tap Next and we will send a 6-digit code to this number.
                </p>

                {phoneOtpSent && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Enter the 6-digit code
                    </label>
                    <div className="mt-2 flex justify-between gap-2">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <input
                          key={index}
                          ref={(node) => {
                            otpRefs.current[index] = node;
                          }}
                          type="text"
                          inputMode="numeric"
                          autoComplete={index === 0 ? "one-time-code" : "off"}
                          maxLength={index === 0 ? 6 : 1}
                          aria-label={`Digit ${index + 1}`}
                          className="h-12 w-11 rounded-md border border-gray-300 text-center text-lg font-semibold text-gray-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 sm:w-12"
                          value={phoneOtp[index] || ""}
                          onChange={(e) => updateOtpAt(index, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Backspace" && !phoneOtp[index] && index > 0) {
                              const next = Array.from({ length: 6 }, (_, i) => phoneOtp[i] || "");
                              next[index - 1] = "";
                              setPhoneOtp(next.join(""));
                              otpRefs.current[index - 1]?.focus();
                            }
                          }}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleSendPhoneOtp}
                      disabled={phoneOtpSending || phoneOtpCooldownSeconds > 0}
                      className="mt-3 text-sm font-medium text-green-700 hover:text-green-800 disabled:text-gray-400"
                    >
                      {phoneOtpSending
                        ? "Sending..."
                        : phoneOtpCooldownSeconds > 0
                        ? `Resend code in ${phoneOtpCooldownSeconds}s`
                        : "Resend code"}
                    </button>
                  </div>
                )}

                {phoneOtpError && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {phoneOtpError}
                  </div>
                )}

                {phoneNumberExists && (
                  <div className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                    <p>
                      Already have an account with this phone number?{" "}
                      <Link
                        to="/login"
                        className="font-semibold text-green-700 underline"
                      >
                        Sign in instead
                      </Link>{" "}
                      or reset your password to regain access.
                    </p>
                  </div>
                )}

                {phoneOtpMessage && (
                  <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                    {phoneOtpMessage}
                  </div>
                )}
              </div>
            </div>

            <div className={step === "details" ? "" : "hidden"}>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required={step === "details"}
                  placeholder="Create a strong password"
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className={step === "details" ? "" : "hidden"}>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm Password
              </label>
              <div className="relative mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required={step === "details"}
                  placeholder="Confirm your password"
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none"
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {formData.confirmPassword ? (
                <p className={`mt-1 text-xs ${formData.password === formData.confirmPassword ? "text-green-700" : "text-red-600"}`}>
                  {formData.password === formData.confirmPassword ? "Passwords match" : "Passwords do not match"}
                </p>
              ) : null}
            </div>

            <div className={step === "details" ? "" : "hidden"}>
              <label
                htmlFor="referralCode"
                className="block text-sm font-medium text-gray-700"
              >
                Referral Code <span className="text-gray-500 text-xs font-normal">(Optional)</span>
              </label>
              <input
                id="referralCode"
                name="referralCode"
                type="text"
                placeholder="Enter referral code if you have one"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm uppercase"
                value={formData.referralCode}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    referralCode: e.target.value.toUpperCase().trim(),
                  });
                }}
                maxLength={6}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the referral code of the person who referred you (if any)
              </p>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={
                loading ||
                phoneOtpSending ||
                phoneOtpVerifying ||
                (step === "phone" && !formatPhoneNumberForApi(formData.phoneNumber)) ||
                (step === "phone" && phoneOtpSent && phoneOtp.length !== 6)
              }
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              {step === "details"
                ? loading
                  ? "Creating account..."
                  : "Register"
                : phoneOtpVerifying
                ? "Verifying..."
                : phoneOtpSending
                ? "Sending code..."
                : phoneOtpSent
                ? "Verify"
                : "Next"}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
    </div>
  );
};

export default Register;
